import React, { useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import Footer from '../components/Footer';
import axios from "axios";
import Swal from "sweetalert2";

const BecomePartnerPage = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    contactPerson: "",
    email: "",
    phone: "",
    location: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateEmail = (email) => {
    if (!email) return 'Please enter your email';
    const re = /^\S+@\S+\.\S+$/;
    return re.test(email) ? '' : 'Enter a valid email address';
  };

  const validatePhone = (phone) => {
    if (!phone) return 'Please enter your mobile number';
    const re = /^[0-9]{10}$/;
    return re.test(phone) ? '' : 'Enter a valid 10-digit phone number';
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'contactPerson':
        return value ? (value.trim().length >= 2 ? '' : 'Name must be at least 2 characters') : 'Please enter your name';
      case 'email':
        return validateEmail(value);
      case 'phone':
        return validatePhone(value);
      case 'location':
        return value ? (value.trim().length >= 2 ? '' : 'Location must be at least 2 characters') : 'Please enter your location';
      case 'message':
        return value ? '' : 'Please enter your message';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, formData[name]);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateForm = () => {
    const fields = ['contactPerson', 'email', 'phone', 'location', 'message'];
    const newErrors = {};
    fields.forEach((f) => {
      newErrors[f] = validateField(f, formData[f]);
    });
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    const formErrors = validateForm();
    setErrors(formErrors);
    setTouched({
      contactPerson: true,
      email: true,
      phone: true,
      location: true,
      message: true,
    });

    const hasErrors = Object.values(formErrors).some((error) => error);
    if (hasErrors) return;

    setLoading(true);
    try {
      const payload = {
        name: formData.contactPerson,
        email: formData.email,
        mobile: formData.phone,
        city: formData.location,
        message: formData.message,
      };
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/madhuprabha/partnership-request`,
        payload
      );

      if (response.data.status === "SUCCESS") {
        Swal.fire({
          title: "Request Submitted!",
          text: response.data.message,
          icon: "success",
          confirmButtonText: "OK",
        });
        setFormData({ contactPerson: "", email: "", phone: "", location: "", message: "" });
        setErrors({});
        setTouched({});
      } else {
        Swal.fire({
          title: "Error",
          text: response.data.message || "Something went wrong.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Message Failed!",
        text: error.response?.data?.message || "Something went wrong. Please try again later.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative flex items-center justify-center text-white overflow-hidden"
        style={{
          backgroundImage: "url('/images/Become-Patner1.jpg')",
          backgroundSize: "100% 100%",
          backgroundPosition: "center",
          height: '90vh',
        }}
      >
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-20 w-72 h-72 bg-white rounded-full blur-2xl"></div>
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-white rounded-full blur-2xl"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6">
            Become Our <br /> Trusted Partner
          </h1>
          <p className="text-lg md:text-xl text-white max-w-3xl mx-auto leading-relaxed">
            Join hands with Madhuprabha Constructions Pvt. Ltd. and grow your business while contributing to our vision of building excellence.
          </p>
        </div>
      </section>

      {/* Application Form */}
      <section id="partner-form" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 items-stretch">
            <div className="flex flex-col justify-center h-full">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 -mt-[50px]">
                Business Opportunity
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                People interested in getting into joint ventures, real estate property dealings or property development and investment in our projects can contact us by filling this form below. Thank you.
              </p>
              <div className="relative rounded-2xl p-px bg-['#011321'] h-full">
                <div className="rounded-2xl bg-white p-6 shadow-lg border border-['#011321'] h-full">
                  <form onSubmit={handleSubmit} className="space-y-4 h-full flex flex-col justify-between">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        name="contactPerson"
                        value={formData.contactPerson}
                        onChange={(e) => {
                        const onlyText = e.target.value.replace(/[^A-Za-z\s]/g, "");
                        handleChange({ target: { name: "contactPerson", value: onlyText } });
                        }}
                        onBlur={handleBlur}
                        type="text"
                      className="w-full pl-3 pr-4 py-3 border border-[#011321] rounded-xl focus:ring-2 focus:ring-[#011321] focus:border-transparent outline-none transition-all duration-300 resize-none"
                        placeholder="Enter your name"
                      />
                      {touched.contactPerson && errors.contactPerson && <p className="mt-1 text-xs text-red-600">{errors.contactPerson}</p>}
                    </div>


                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        type="email"
                      className="w-full pl-3 pr-4 py-3 border border-[#011321] rounded-xl focus:ring-2 focus:ring-[#011321] focus:border-transparent outline-none transition-all duration-300 resize-none"
                        placeholder="company@email.com"
                      />
                      {touched.email && errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          if (value.length <= 10) {
                            handleChange({ target: { name: "phone", value } });
                          }
                        }}
                        onBlur={handleBlur}
                        type="tel"
                        maxLength="10"
                        className="w-full pl-3 pr-4 py-3 border border-[#011321] rounded-xl focus:ring-2 focus:ring-[#011321] focus:border-transparent outline-none transition-all duration-300 resize-none"
                        placeholder="9876543210"
                      />
                      {touched.phone && errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
                      <p className="text-xs text-gray-500 mt-1">Enter 10 digit mobile number only</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                      <input
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        type="text"
                      className="w-full pl-3 pr-4 py-3 border border-[#011321] rounded-xl focus:ring-2 focus:ring-[#011321] focus:border-transparent outline-none transition-all duration-300 resize-none"
                        placeholder="City, State"
                      />
                      {touched.location && errors.location && <p className="mt-1 text-xs text-red-600">{errors.location}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tell us about your business</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        rows="4"
                        className="w-full pl-3 pr-4 py-3 border border-[#011321] rounded-xl focus:ring-2 focus:ring-[#011321] focus:border-transparent outline-none transition-all duration-300 resize-none"
                        placeholder="Describe your company, capabilities, and why you want to partner with us"
                      ></textarea>
                      {touched.message && errors.message && <p className="mt-1 text-xs text-red-600">{errors.message}</p>}
                    </div>

                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-500">By applying, you agree to our partnership terms and conditions.</p>
                      <button
                        type="submit"
                        className="group inline-flex items-center justify-center w-full md:w-auto rounded-lg bg-[#011321] px-6 py-3 text-white text-sm font-semibold shadow hover:bg-[#022840] transition-colors"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin text-white mr-2" />
                            <span>Sending...</span>
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5 text-white mr-1 group-hover:text-white group-hover:translate-x-1 transition-transform duration-300" />
                           <span>Submit Application</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className="flex items-stretch h-full">
              <div className="relative w-full h-full">
                <div className="absolute -inset-6 bg-gradient-to-br from-red-100 to-transparent rounded-3xl -z-10" />
                <div className="h-full w-full rounded-3xl overflow-hidden shadow-xl border border-red-100">
                  <img
                    src="/images/vecteezy_business-people-view_12110410.jpg"
                    alt="Partnership with Madhuprabha Construction"
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BecomePartnerPage;
